"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];

// "2026-10-05" → "2026年10月5日（月）"（端末のタイムゾーンに左右されない）
const formatDate = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  const w = WEEKDAYS[new Date(y, m - 1, d).getDay()];
  return `${y}年${m}月${d}日（${w}）`;
};

// 端末の現地日付を "YYYY-MM-DD" で返す（toISOString は UTC 基準なので使わない）
const todayString = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
};
const labelClass = "block text-sm font-bold text-foreground font-default mb-2";
const fieldClass = "w-full border border-input rounded-md px-3 py-2 text-sm font-default bg-background text-foreground";
export default function ContactFormSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    website: "",
    inquiry_type: "ご予約",
    preferred_dates: [] as string[],
    message: ""
  });
  const [pendingDate, setPendingDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const addDate = () => {
    if (!pendingDate || pendingDate < todayString()) return;
    setFormData(prev => prev.preferred_dates.includes(pendingDate) ? prev : {
      ...prev,
      preferred_dates: [...prev.preferred_dates, pendingDate].sort()
    });
    setPendingDate("");
  };
  const removeDate = (date: string) => {
    setFormData(prev => ({
      ...prev,
      preferred_dates: prev.preferred_dates.filter(d => d !== date)
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    // 「追加」を押さずに送信した日付も含める
    const dates = [...formData.preferred_dates];
    if (pendingDate && pendingDate >= todayString() && !dates.includes(pendingDate)) dates.push(pendingDate);
    dates.sort();
    const message = dates.length > 0 ? `【ご予約希望日】\n${dates.map(formatDate).join("\n")}\n\n【メッセージ】\n${formData.message}` : formData.message;
    try {
      const response = await fetch("https://yotsuya-sc-room.com/wp-json/custom/v1/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          website: formData.website,
          inquiry_type: formData.inquiry_type,
          message
        })
      });
      if (response.ok) {
        setIsSubmitted(true);
        const w = window as any;
        if (typeof w.gtag === "function") {
          w.gtag("event", "contact_form_submit", {
            inquiry_type: formData.inquiry_type
          });
        }
        document.getElementById("contact-form-section")?.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      } else {
        const data = await response.json().catch(() => null);
        setError(data?.message || "送信に失敗しました。お手数ですがお電話にてご連絡ください。");
      }
    } catch (err) {
      setError("送信に失敗しました。お手数ですがお電話にてご連絡ください。");
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSubmitted) {
    return <section id="contact-form-section" className="bg-secondary/30 py-16 md:py-24 scroll-mt-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground font-default leading-tight mb-4">送信が完了しました</h2>
          <Separator className="w-12 h-0.5 bg-primary mb-8" />
          <div className="space-y-6">
            <p className="text-foreground leading-relaxed font-default">ご連絡ありがとうございます。当日中、または翌日までにお返事いたします。</p>
            <p className="text-foreground leading-relaxed font-default">ご入力いただいたメールアドレス宛に、受付確認の自動返信をお送りしました。届いていない場合は、迷惑メールフォルダもご確認ください。</p>
          </div>
        </div>
      </section>;
  }
  return <section id="contact-form-section" className="bg-secondary/30 py-16 md:py-24 scroll-mt-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground font-default leading-tight mb-4">ご予約・お問い合わせフォーム</h2>
        <Separator className="w-12 h-0.5 bg-primary mb-8" />
        <p className="text-foreground leading-relaxed font-default mb-10">必要事項をご記入のうえ、「送信する」を押してください。ご入力いただいたメールアドレス宛に、受付確認の自動返信をお送りします。</p>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className={labelClass}>お名前 <span className="text-destructive">*</span></label>
              <Input id="name" name="name" required value={formData.name} onChange={handleChange} autoComplete="name" className="bg-background" />
            </div>
            <div>
              <label htmlFor="phone" className={labelClass}>電話番号</label>
              <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} autoComplete="tel" className="bg-background" />
            </div>
          </div>

          <div>
            <label htmlFor="email" className={labelClass}>メールアドレス <span className="text-destructive">*</span></label>
            <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} autoComplete="email" className="bg-background" />
          </div>

          <div>
            <label htmlFor="inquiry_type" className={labelClass}>ご用件 <span className="text-destructive">*</span></label>
            <select id="inquiry_type" name="inquiry_type" required value={formData.inquiry_type} onChange={handleChange} className={fieldClass}>
              <option value="ご予約">ご予約</option>
              <option value="お問い合わせ">お問い合わせ</option>
            </select>
          </div>

          <div>
            <label htmlFor="date" className={labelClass}>ご予約希望日（複数可）</label>
            <div className="flex gap-2">
              <input id="date" type="date" value={pendingDate} min={todayString()} onChange={e => setPendingDate(e.target.value)} className={fieldClass} />
              <Button type="button" variant="outline" onClick={addDate} disabled={!pendingDate} className="shrink-0 border-primary text-primary-heading hover:bg-primary/10 rounded-lg">追加</Button>
            </div>
            <p className="mt-2 text-sm text-muted-foreground font-default">日付を選んで「追加」を押してください。ご希望の時間帯があれば、メッセージ欄にご記入ください。</p>
            {formData.preferred_dates.length > 0 && <ul className="mt-3 flex flex-wrap gap-2">
                {formData.preferred_dates.map(date => <li key={date} className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-background px-4 py-1.5 text-sm text-primary-heading">
                    {formatDate(date)}
                    <button type="button" onClick={() => removeDate(date)} aria-label={`${formatDate(date)}を削除`} className="font-bold hover:opacity-70">×</button>
                  </li>)}
              </ul>}
          </div>

          <div>
            <label htmlFor="message" className={labelClass}>メッセージ</label>
            <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="ご相談の内容やご質問を、差し支えない範囲でご記入ください。" className="min-h-[150px] bg-background" />
          </div>

          <div className="bg-background border border-primary/40 rounded-lg px-5 py-4">
            <p className="text-sm text-foreground font-default leading-relaxed mb-3">ご入力いただいた個人情報は、ご予約・お問い合わせへの対応のみに使用し、ご本人の同意なく第三者に提供することはありません。詳しくは<a href="https://yotsuya-sc-room.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-primary-heading underline underline-offset-4">プライバシーポリシー</a>をご確認ください。</p>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="privacy-policy" required className="w-4 h-4 accent-primary" />
              <label htmlFor="privacy-policy" className="text-sm text-foreground font-default">プライバシーポリシーに同意します <span className="text-destructive">*</span></label>
            </div>
          </div>

          {/* ハニーポット：人間には見えない。ボット対策 */}
          <div className="absolute left-[-9999px] w-px h-px overflow-hidden" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" value={formData.website} onChange={handleChange} />
          </div>

          {error && <p className="text-destructive text-sm font-default">{error}</p>}

          <div className="flex justify-center pt-2">
            <Button type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto px-12 shadow-md rounded-lg">{isSubmitting ? "送信中..." : "送信する"}</Button>
          </div>
        </form>
      </div>
    </section>;
}